/* Label & Leaf — local YuNet photo check shared by storybooks, colouring
   books and bundles. The lazy, pinned runtime runs in a disposable worker.
   Photos never leave the browser for this check. No identity or age matching.
   assess() gives a verdict: block a missing or ambiguous face, an unusable
   frame or an unreadable file; warn on a sideways photo, a smaller face in
   the background, measured blur or a check that could not run. Clear mild
   head turns remain accepted. */
(function () {
  'use strict';

  /* Three switches. VERDICTS lets assess() compute a verdict; it gates the
     preview offer (spec 6.5), through assess() and warmUp(true), and every
     order check. BLOCK_ORDERS is the owner's 2026-09-26 switch to refuse a
     photo on the order path; it stays off. WARN_ORDERS (owner, 2026-10-27)
     shows the same face result as a gentle warning and lets the shopper
     order anyway. With both order switches off, check() accepts every photo
     without loading the detector. An unusable photo is held by the Order
     Desk's source-face review before any generation, and the intake still
     refuses an unreadable or incomplete file. Set BLOCK_ORDERS only after a
     week of verdict counts shows what it would have refused. */
  var VERDICTS = true;
  var BLOCK_ORDERS = false;
  var WARN_ORDERS = true;
  // True only when check() runs the detector for an order.
  var enabled = VERDICTS && (BLOCK_ORDERS || WARN_ORDERS);
  var orders = !enabled ? 'off' : BLOCK_ORDERS ? 'block' : 'warn';
  var SECOND_FACE_BLOCK_RATIO = 0.6;
  var ASSESS_TIMEOUT_MS = 15000;
  var MIN_SCORE = 0.6;
  var BLOCK_FACE_FRACTION = 0.08;
  var EDGE_OVERHANG_BLOCK = 0.2;
  var DUPLICATE_IOU = 0.3;
  var BLUR_WARN = 25;
  var ANALYSIS_MAX_EDGE = 640;
  var FACE_CROP_EDGE = 128;
  var LOAD_TIMEOUT_MS = 45000;

  var MESSAGES = {
    ok: '✓ Lovely — this photo is ready to use.',
    'no-face': 'We couldn’t find a clear face in this photo. Please choose one where their face is clearly visible and facing the camera.',
    'face-small': 'Their face is quite small in this photo. Please choose a closer photo where their face fills more of the frame.',
    'face-cut-off': 'Their face looks cut off at the edge. Please choose a photo with their whole face in view.',
    'multiple-faces': 'We can see more than one face. Please choose a photo of just your child.',
    'background-face': 'We can see someone else in the background. A photo of just your child works best.',
    unreadable: 'We couldn’t read that image. Please choose another.',
    'photo-rotated': 'This photo looks sideways or upside down. Please rotate it upright and upload it again.',
    'detector-unavailable': 'We couldn’t check this photo. Please choose it again to retry. If this keeps happening, refresh the page and try again.',
    blurry: 'This photo looks a little soft or blurry. A sharper photo gives a better likeness — you can continue or choose another.'
  };

  /* ================= pure policy ================= */

  function score(face) {
    var p = face && face.probability;
    if (Array.isArray(p)) p = p[0];
    return typeof p === 'number' ? p : 0;
  }

  function boxOf(face) {
    var tl = face.topLeft || [0, 0];
    var br = face.bottomRight || [0, 0];
    return {
      left: Math.min(tl[0], br[0]),
      top: Math.min(tl[1], br[1]),
      right: Math.max(tl[0], br[0]),
      bottom: Math.max(tl[1], br[1]),
      width: Math.abs(br[0] - tl[0]),
      height: Math.abs(br[1] - tl[1])
    };
  }

  function result(level, code, faces, face, sharpness) {
    return {
      level: level,
      code: code,
      message: MESSAGES[code] || MESSAGES.ok,
      faceCount: faces.length,
      face: face || null,
      sharpness: typeof sharpness === 'number' && isFinite(sharpness) ? sharpness : null
    };
  }

  function intersectionOverUnion(a, b) {
    var left = Math.max(a.left, b.left);
    var top = Math.max(a.top, b.top);
    var right = Math.min(a.right, b.right);
    var bottom = Math.min(a.bottom, b.bottom);
    var inter = Math.max(0, right - left) * Math.max(0, bottom - top);
    var union = a.width * a.height + b.width * b.height - inter;
    return union > 0 ? inter / union : 0;
  }

  // Score-ranked IoU suppression only: a small, different face whose centre
  // falls inside a larger box must not disappear from the subject count.
  function confidentFaces(detections) {
    var faces = (detections || []).filter(function (face) { return score(face) >= MIN_SCORE; });
    faces.sort(function (a, b) { return score(b) - score(a); });
    var kept = [];
    faces.forEach(function (face) {
      if (!kept.some(function (other) { return intersectionOverUnion(boxOf(face), boxOf(other)) > DUPLICATE_IOU; })) kept.push(face);
    });
    return kept.sort(function (a, b) { var ba = boxOf(a), bb = boxOf(b); return bb.width * bb.height - ba.width * ba.height; });
  }

  function evaluate(detections, width, height, sharpness) {
    var faces = confidentFaces(detections);
    if (!faces.length) return result('block', 'no-face', faces, null, sharpness);
    var face = faces[0];
    var box = boxOf(face);
    // A second face nearly as large as the main one leaves the subject unclear.
    if (faces.length > 1 && boxOf(faces[1]).width >= SECOND_FACE_BLOCK_RATIO * box.width) return result('block', 'multiple-faces', faces, face, sharpness);
    var shortSide = Math.max(1, Math.min(width, height));
    var fraction = box.width / shortSide;
    if (fraction < BLOCK_FACE_FRACTION) return result('block', 'face-small', faces, face, sharpness);

    var overhang = Math.max(
      -box.left / Math.max(1, box.width),
      (box.right - width) / Math.max(1, box.width),
      -box.top / Math.max(1, box.height),
      (box.bottom - height) / Math.max(1, box.height)
    );
    if (overhang > EDGE_OVERHANG_BLOCK) return result('block', 'face-cut-off', faces, face, sharpness);

    /* A landmark-based pose estimate or a small box overhang does not
       establish that a detected face is unclear. A distinctly smaller face is
       background, not a second subject: warn about it, then on measured
       softness, instead of asking for an ideal composition. */
    if (faces.length > 1) return result('warn', 'background-face', faces, face, sharpness);
    if (typeof sharpness === 'number' && isFinite(sharpness) && sharpness < BLUR_WARN) return result('warn', 'blurry', faces, face, sharpness);
    return result('ok', 'ok', faces, face, sharpness);
  }

  /* Variance of the 4-neighbour Laplacian over a greyscale buffer. Flat
     areas contribute nothing; edges and texture push it up, so a soft or
     defocused crop scores low. */
  function laplacianVariance(gray, width, height) {
    if (!gray || width < 3 || height < 3) return 0;
    var sum = 0;
    var sumSq = 0;
    var count = 0;
    for (var y = 1; y < height - 1; y += 1) {
      for (var x = 1; x < width - 1; x += 1) {
        var i = y * width + x;
        var lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - width] - gray[i + width];
        sum += lap;
        sumSq += lap * lap;
        count += 1;
      }
    }
    if (!count) return 0;
    var mean = sum / count;
    return sumSq / count - mean * mean;
  }

  /* A flat colour, or nothing visible, means the image rendered as a blank
     canvas: the file decoded but holds no photograph (a failed or empty one). */
  function isBlank(rgba) {
    var min = 255;
    var max = 0;
    var visible = false;
    for (var i = 0; i + 3 < rgba.length; i += 4) {
      if (rgba[i + 3] > 0) visible = true;
      var luminance = 0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2];
      if (luminance < min) min = luminance;
      if (luminance > max) max = luminance;
      if (visible && max - min > 2) return false;
    }
    return !visible || max - min <= 2;
  }

  /* Order path (WARN_ORDERS): the same face findings as a warning, in words
     that fit any hero, child, grown-up or pet. Only the level and the copy
     change; the code stays the one assess() found. */
  var ORDER_MESSAGES = {
    'no-face': 'We couldn’t see a face in this photo. A clear photo of their face works best.',
    'face-small': 'Their face looks small in this photo. A closer photo works best.',
    'face-cut-off': 'Their face looks cut off at the edge. A photo with their whole face works best.',
    'multiple-faces': 'We can see more than one person. A photo of just one person works best.'
  };

  /* A Finding book's hero may be a child, a grown-up or a pet (owner,
     2026-10-28). check(image, { neutral: true }) gives these words instead:
     no "your child", "their face" or "one person" a pet would not fit, as
     the Finding tips say "Just one person or pet". Story pages keep their
     own. The other order warnings (small, cut off, blurry, sideways) already
     fit any hero. */
  var NEUTRAL_MESSAGES = {
    'no-face': 'We couldn’t spot a clear face. A clear, close photo works best.',
    'multiple-faces': 'We can see more than one person. A photo of just them works best.',
    'background-face': 'We can see someone else in the background. A photo of just them works best.'
  };

  /* A face block becomes a warning. An unreadable file stays a block (there is
     no photograph to make a book from). A check that could not run says
     nothing is known, so it passes quietly. Everything else is unchanged. */
  function orderVerdict(verdict) {
    var next = {};
    Object.keys(verdict).forEach(function (key) { next[key] = verdict[key]; });
    if (verdict.level === 'block' && ORDER_MESSAGES[verdict.code]) {
      next.level = 'warn';
      next.message = ORDER_MESSAGES[verdict.code];
      return next;
    }
    if (verdict.code === 'detector-unavailable') {
      next.level = 'ok';
      next.code = 'unchecked';
      next.message = MESSAGES.ok;
      return next;
    }
    return verdict;
  }

  // A warning in neutral words when options.neutral asks for them.
  function neutralVerdict(verdict, options) {
    if (!(options && options.neutral && verdict && verdict.level === 'warn' && NEUTRAL_MESSAGES[verdict.code])) return verdict;
    var next = {};
    Object.keys(verdict).forEach(function (key) { next[key] = verdict[key]; });
    next.message = NEUTRAL_MESSAGES[verdict.code];
    return next;
  }

  /* The check could not run (no module, worker failure, timeout). That is not
     the customer's photo being wrong, so it only warns and never stops them. */
  function fallbackResult(reason) {
    return { level: 'warn', code: 'detector-unavailable', message: MESSAGES['detector-unavailable'], faceCount: -1, face: null, sharpness: null, reason: reason || '' };
  }

  /* ================= browser: lazy detector ================= */

  var vendorUrl = null;
  try {
    var current = typeof document !== 'undefined' && document.currentScript;
    if (current && current.getAttribute) vendorUrl = current.getAttribute('data-ll-photo-check-vendor');
    if (!vendorUrl && typeof document !== 'undefined' && document.querySelector) {
      var tagged = document.querySelector('script[data-ll-photo-check-vendor]');
      if (tagged) vendorUrl = tagged.getAttribute('data-ll-photo-check-vendor');
    }
  } catch (e) { vendorUrl = null; }

  // Existing Liquid tags supply a versioned asset URL. Derive the immutable
  // YuNet asset from that same theme CDN directory; no Liquid rollout is needed.
  if (vendorUrl) vendorUrl = vendorUrl.replace(/ll-photo-check-vendor\.js(?:\?.*)?$/, 'll-photo-check-yunet-v2.js');
  var vendorPromise = null;
  var detectorPromise = null;
  var activeWorker = null;
  var activeWorkerUrl = null;
  var generation = 0;
  var nextId = 0;
  var pending = {};

  function resetWorker() {
    generation += 1;
    if (activeWorker) activeWorker.terminate();
    if (activeWorkerUrl) URL.revokeObjectURL(activeWorkerUrl);
    activeWorker = null;
    activeWorkerUrl = null;
    detectorPromise = null;
    Object.keys(pending).forEach(function (id) { pending[id].reject(new Error('photo-check interrupted')); });
    pending = {};
  }

  function loadVendor() {
    if (window.LLPhotoCheckYuNet) return Promise.resolve(window.LLPhotoCheckYuNet);
    if (vendorPromise) return vendorPromise;
    var script;
    var load = new Promise(function (resolve, reject) {
      if (!vendorUrl) { reject(new Error('photo-check vendor url missing')); return; }
      script = document.createElement('script');
      script.src = vendorUrl;
      script.async = true;
      script.onload = function () {
        if (window.LLPhotoCheckYuNet) resolve(window.LLPhotoCheckYuNet);
        else reject(new Error('photo-check vendor did not register'));
      };
      script.onerror = function () { reject(new Error('photo-check vendor failed to load')); };
      document.head.appendChild(script);
    });
    vendorPromise = withTimeout(load, LOAD_TIMEOUT_MS).catch(function (error) {
      vendorPromise = null;
      if (script) script.remove();
      throw error;
    });
    return vendorPromise;
  }

  function getDetector() {
    if (detectorPromise) return detectorPromise;
    var started = generation;
    var load = loadVendor().then(function (vendor) {
      if (started !== generation) throw new Error('photo-check cancelled');
      return new Promise(function (resolve, reject) {
        var created = vendor.createWorker(), worker = created.worker;
        activeWorker = worker;
        activeWorkerUrl = created.url;
        function fail(error) {
          URL.revokeObjectURL(created.url);
          reject(new Error(error && error.message || 'photo-check worker failed'));
          if (activeWorker === worker) resetWorker();
        }
        worker.onerror = fail;
        worker.onmessageerror = fail;
        worker.onmessage = function (event) {
          if (worker !== activeWorker) return;
          var data = event.data;
          if (data.failed) { fail({ message: data.reason }); return; }
          if (data.ready) {
            URL.revokeObjectURL(created.url);
            activeWorkerUrl = null;
            resolve({ estimateFaces: function (canvas) {
              return new Promise(function (resolve, reject) {
                if (worker !== activeWorker) { reject(new Error('photo-check worker replaced')); return; }
                var pixels = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
                var id = ++nextId;
                pending[id] = { resolve: resolve, reject: reject };
                worker.postMessage({ id: id, pixels: pixels, width: canvas.width, height: canvas.height }, [pixels.buffer]);
              });
            } });
          } else if (pending[data.id]) {
            pending[data.id].resolve(data.result);
            delete pending[data.id];
          }
        };
      });
    });
    detectorPromise = withTimeout(load, LOAD_TIMEOUT_MS).catch(function (error) {
      if (started === generation) resetWorker();
      throw error;
    });
    return detectorPromise;
  }

  function warmUp(forVerdict) {
    if (!(enabled || (forVerdict && VERDICTS))) return;
    try { getDetector().catch(function () { /* assess() offers a retry */ }); } catch (e) { /* assess() reports it */ }
  }

  // The browser applies EXIF orientation while decoding the original upload.
  // Keep a bounded analysis copy; never rewrite the uploaded file silently.
  function analysisCanvas(image) {
    var naturalWidth = image.naturalWidth || image.videoWidth || image.width;
    var naturalHeight = image.naturalHeight || image.videoHeight || image.height;
    var scale = Math.min(1, ANALYSIS_MAX_EDGE / Math.max(naturalWidth, naturalHeight));
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(naturalHeight * scale));
    var ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  function faceSharpness(canvas, face) {
    try {
      var box = boxOf(face);
      var left = Math.max(0, Math.floor(box.left));
      var top = Math.max(0, Math.floor(box.top));
      var right = Math.min(canvas.width, Math.ceil(box.right));
      var bottom = Math.min(canvas.height, Math.ceil(box.bottom));
      if (right - left < 8 || bottom - top < 8) return null;
      var crop = document.createElement('canvas');
      crop.width = FACE_CROP_EDGE;
      crop.height = FACE_CROP_EDGE;
      var ctx = crop.getContext('2d');
      ctx.drawImage(canvas, left, top, right - left, bottom - top, 0, 0, FACE_CROP_EDGE, FACE_CROP_EDGE);
      var data = ctx.getImageData(0, 0, FACE_CROP_EDGE, FACE_CROP_EDGE).data;
      var gray = new Float32Array(FACE_CROP_EDGE * FACE_CROP_EDGE);
      for (var i = 0, j = 0; i < data.length; i += 4, j += 1) {
        gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }
      return laplacianVariance(gray, FACE_CROP_EDGE, FACE_CROP_EDGE);
    } catch (e) {
      return null;
    }
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var done = false;
      var timer = window.setTimeout(function () {
        if (done) return;
        done = true;
        reject(new Error('photo-check timed out'));
      }, ms);
      promise.then(function (value) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        resolve(value);
      }, function (error) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        reject(error);
      });
    });
  }

  function assess(image) {
    if (!VERDICTS) return Promise.resolve(result('ok', 'ok', [], null, null));
    var started = generation;
    var decoded;
    try {
      decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
    } catch (e) {
      decoded = Promise.reject(e);
    }
    // A file that will not decode is unreadable; any later failure is the
    // check's own, which falls through to fallbackResult below.
    var run = decoded.then(function () {
      var canvas = analysisCanvas(image);
      if (isBlank(canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data)) {
        return result('block', 'unreadable', [], null, null);
      }
      return getDetector().then(function (model) {
        return model.estimateFaces(canvas).then(function (detected) {
          var faces = confidentFaces(detected.faces);
          if (faces.length && detected.rotation) {
            // The faces are in the turned view's own coordinates: a block
            // there stands, otherwise the turn is only a warning.
            var turned = evaluate(faces, detected.width, detected.height, null);
            return turned.level === 'block' ? turned : result('warn', 'photo-rotated', faces, faces[0], null);
          }
          var sharpness = faces.length ? faceSharpness(canvas, faces[0]) : null;
          return evaluate(faces, canvas.width, canvas.height, sharpness);
        });
      });
    }, function () {
      return result('block', 'unreadable', [], null, null);
    });
    return withTimeout(run, ASSESS_TIMEOUT_MS).catch(function (error) {
      if (started === generation) resetWorker();
      return fallbackResult(error && error.message ? error.message : 'unknown');
    });
  }

  // Orders: while BLOCK_ORDERS is off, WARN_ORDERS turns a face block into a gentle warning (an unreadable file still blocks, and a check that could not run passes quietly); with both off every photo is accepted without loading the detector.
  // options.neutral: a Finding page's words (NEUTRAL_MESSAGES).
  function check(image, options) {
    if (BLOCK_ORDERS) return assess(image).then(function (verdict) { return neutralVerdict(verdict, options); });
    if (WARN_ORDERS) return assess(image).then(function (verdict) { return neutralVerdict(orderVerdict(verdict), options); });
    return Promise.resolve(result('ok', 'ok', [], null, null));
  }

  window.LLPhotoCheck = {
    detector: 'yunet-2026may',
    enabled: enabled,
    verdicts: VERDICTS,
    orders: orders,
    MESSAGES: MESSAGES,
    ORDER_MESSAGES: ORDER_MESSAGES,
    NEUTRAL_MESSAGES: NEUTRAL_MESSAGES,
    evaluate: evaluate,
    orderVerdict: orderVerdict,
    neutralVerdict: neutralVerdict,
    laplacianVariance: laplacianVariance,
    isBlank: isBlank,
    fallbackResult: fallbackResult,
    warmUp: warmUp,
    check: check,
    assess: assess
  };
}());
